import React, { ReactNode } from "react";
import {
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableColumn,
  TableHeaderItem,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import {
  areFiltersSelected,
  NoResultsIndicator,
  useDeviceFilterValue
} from "elements";
import { generateTableHeaders } from "utilities/tableUtils";
import { TABLES } from "store/userMeta";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { ImportGroupRowData } from "./importGroupsTypes";

export const ImportGroupsTable: React.FC<{
  userGroups: Array<ImportGroupRowData>;
  userCanEdit: boolean;
  selectedItems: any;
  setSelectedItems: any;
  loading: boolean;
  fetching: boolean;
  shown: TableColumn[];
}> = ({
  userGroups,
  userCanEdit,
  selectedItems,
  setSelectedItems,
  loading,
  fetching,
  shown
}) => {
  const { t } = useTranslation();

  const toggleItem = toggleItemWithSet(
    userGroups.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const [filterState, , resetFilters] = useDeviceFilterValue();

  const getTableHeaders = (column: string, title: string) => {
    return <TableHeaderItem fieldName={column} title={title} key={column} />;
  };

  // there is one additional column besides those user choose to display (checkbox to select rows)
  const columnCount = shown.length + 1;

  return (
    <div className="">
      <table className="table custom-table table-filterable">
        <TableBulkItemHeader
          showChecks={userCanEdit}
          checked={itemChecked(HEADER_ID)}
          onCheck={() =>
            toggleAll(
              userGroups.map(group => group.id),
              selectedItems,
              setSelectedItems
            )
          }
          onClearFilters={resetFilters}
          showClearFilters={areFiltersSelected(filterState)}
        >
          {generateTableHeaders(shown, getTableHeaders)}
        </TableBulkItemHeader>
        <TableBody
          className="scrollable-content-sm"
          loading={loading}
          cols={columnCount}
          accommodateScrollbar
        >
          {userGroups.map((group, i) => {
            return (
              <ExternalUserGroupRow
                data={group}
                key={group.id}
                onCheck={() => toggleItem(group.id)}
                checked={itemChecked(group.id)}
                showChecks={userCanEdit}
                shownColumns={shown.map(column => column.key)}
              />
            );
          })}
        </TableBody>
      </table>
      {!fetching && !userGroups.length ? (
        <NoResultsIndicator title={t("NO_USER_GROUPS")} />
      ) : null}
    </div>
  );
};

type UserGroupRowProps = {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
  showChecks: boolean;
  data: ImportGroupRowData;
};

export const ExternalUserGroupRow: React.FC<UserGroupRowProps> = ({
  data,
  checked,
  showChecks,
  onCheck,
  shownColumns
}) => {
  const { COLUMNS } = TABLES.EXTERNAL_USER_GROUPS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = handleNullFields(data.name);
        break;
      case COLUMNS.DESCRIPTION:
        content = handleNullFields(data?.description);
        break;
    }

    return <td key={column}>{content}</td>;
  };
  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(data.id)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};
